import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, Signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  OpenSearchRequest,
  OpenSearchRequestFilter,
  OpenSearchRequestFilterOperator,
} from '@libs/shared/domain/aggregations/opensearch/open-search-query.schema';
import { LogService } from '@services/log.service';
import { DateRange } from '@shared/components/chitin/molecules/daterange-picker/date-range';
import { DataGridFilterChanged } from '@shared/components/data-grid/data-grid-events';
import { PaginatorComponent } from '@shared/components/data-table/paginator/paginator.component';
import { GridColumnFilterType } from '@shared/models/grid-column.model';
import { OpenSearchPage, OpenSearchQueryRange, OpenSearchQueryResult, OpenSearchSimpleSort } from '@shared/providers/opensearch-api';
import { OpenSearchClient } from '@shared/providers/opensearch-client';
import { cloneDeep, isEqual, isObject } from 'lodash';
import moment from 'moment';
import { map, Observable, of, startWith } from 'rxjs';

@Injectable()
export class OpenSearchService {
  protected client = inject(OpenSearchClient);
  protected logger = inject(LogService);

  createInfResource<T>(
    observable: (
      filter: OpenSearchRequestFilter[],
      page: OpenSearchPage,
      sort: OpenSearchSimpleSort,
    ) => Observable<OpenSearchQueryResult<T>>,
    filter: Signal<OpenSearchRequestFilter[]>,
    page: Signal<OpenSearchPage>,
    sort: Signal<OpenSearchSimpleSort>,
    paginator: Signal<PaginatorComponent | undefined>,
  ) {
    let data: T[] = [];
    let lastFilter = undefined as OpenSearchRequestFilter[] | undefined;
    let lastPage = undefined as OpenSearchPage | undefined;
    let lastTotal = 0;

    return rxResource({
      defaultValue: { items: [] as T[], total: 0, loading: false, error: undefined as HttpErrorResponse | undefined },
      params: () => ({ filter: filter(), page: page(), sort: sort() }),
      stream: ({ params }) => {
        let page = params.page;
        let replace = false;

        if (lastFilter && !isEqual(lastFilter, params.filter)) {
          data = [];
          lastTotal = 0;
          page = { from: 0, size: page.size };
          paginator()?.reset();
          replace = true;
        } else if (lastPage && isEqual(lastPage, params.page)) {
          // Treat page resets and page-size changes as replace, not append.
          page = { from: 0, size: Math.max(data.length, params.page.size) };
          replace = true;
        } else if (params.page.from === 0) {
          // Page reset or page-size change: fresh first-page result replaces existing rows.
          data = [];
          lastTotal = 0;
          replace = true;
        }

        const currentData = [...data];
        const currentTotal = lastTotal;

        return observable(params.filter, page, params.sort).pipe(
          map(result => {
            if (replace) {
              data = result.items;
            } else {
              data.push(...result.items);
            }
            lastFilter = params.filter;
            lastPage = params.page;
            lastTotal = result.total;
            paginator()?.setTotal(result.total);
            return { items: data, total: result.total, loading: false, error: result.error };
          }),
          startWith({ items: currentData, total: currentTotal, loading: true, error: undefined }),
        );
      },
    });
  }

  createSearchSort(sort: OpenSearchSimpleSort): OpenSearchRequest['sort'] {
    return Object.entries(sort).map(([key, order]) => {
      return { [key]: { order } };
    });
  }

  applyDataGridFilterChange(input: OpenSearchRequestFilter[], filter: DataGridFilterChanged): OpenSearchRequestFilter[] {
    let filters = cloneDeep(input);
    const field = filter.column.filterField || filter.column.field;
    const filterType = filter.column.filterType || 'equal';
    const existingFilter = filters.find(f => f.field === field);
    if (filter.value) {
      const value = this.mapDataGridValue(filterType, field, filter.value);
      if (existingFilter) {
        existingFilter.value = value;
        existingFilter.operator = this.mapDataGridOperator(filterType);
        existingFilter.caseInsensitive = filter.column.filterCaseInsensitive;
      } else {
        filters.push({
          field,
          value,
          operator: this.mapDataGridOperator(filterType),
          caseInsensitive: filter.column.filterCaseInsensitive,
        });
      }
    } else {
      if (existingFilter) {
        filters = filters.filter(f => f.field !== field);
      }
    }
    return filters;
  }

  getFilterWithPrevious(filter: OpenSearchRequestFilter[], field = 'created') {
    const dateFilter = filter.find(f => f.field === field && f.operator === 'range');

    if (!dateFilter) {
      console.error(`No ${field} date range filter found. Unable to calculate previous date range.`);
      return filter;
    }

    const { gte, lte } = OpenSearchService.extractDateRange(dateFilter, field);
    const diff = lte.getTime() - gte.getTime();
    const previousGte = moment(gte).subtract(diff, 'milliseconds').toDate();
    const previousLte = moment(lte).subtract(diff, 'milliseconds').toDate();

    const previousDateFilter: OpenSearchRequestFilter = {
      field: field,
      operator: 'range',
      value: {
        [field]: {
          gte: previousGte.toISOString(),
          lte: previousLte.toISOString(),
        },
      },
    };

    return [...filter.filter(f => !(f.field === field && f.operator === 'range')), previousDateFilter];
  }

  mapDataGridOperator(operator: GridColumnFilterType): OpenSearchRequestFilterOperator {
    const map: Record<GridColumnFilterType, OpenSearchRequestFilterOperator> = {
      contains: 'contains',
      in: 'in',
      equal: 'equal',
      range: 'range',
      beginsWith: 'startsWith',
      daterange: 'range',
    };
    return map[operator];
  }

  mapDataGridValue(type: GridColumnFilterType, field: string, value: unknown): unknown {
    const isRange = type === 'range' || type === 'daterange';
    const rangeValue = type === 'daterange' ? this.mapDateRangeValue(value) : value;

    return isRange ? { [field]: rangeValue } : value;
  }

  mapDateRangeValue(value: unknown): OpenSearchQueryRange {
    const dateRange = value as Partial<DateRange>;
    const isPickerValue = isObject(dateRange) && ('from' in dateRange || 'to' in dateRange);

    return isPickerValue
      ? {
          gte: dateRange.from ? moment(dateRange.from).toISOString() : undefined,
          lte: dateRange.to ? moment(dateRange.to).toISOString() : undefined,
        }
      : (value as OpenSearchQueryRange);
  }

  /**
   * @deprecated Use processSearchError instead
   * @see processSearchError
   */
  protected processSimpleSearchError<T>(error: HttpErrorResponse) {
    return this.processSearchError<T>(error);
  }

  protected processSearchError<T>(error: HttpErrorResponse) {
    this.logger.error(error);
    return of(this.getEmptyResult<T>(error));
  }

  protected processCountError(error: HttpErrorResponse) {
    this.logger.error(error);
    return of({ count: 0 });
  }

  protected getEmptyResult<T>(error: HttpErrorResponse): OpenSearchQueryResult<T> {
    return { error, items: [], total: 0 };
  }

  static extractDateRange(filter: OpenSearchRequestFilter, field: string): { gte: Date; lte: Date } {
    const dateRange = filter.value as { [key: string]: { gte: string; lte: string } };
    const fieldRange = dateRange[field];
    if (!fieldRange?.gte || !fieldRange?.lte) {
      throw new Error(`Invalid date range structure for field "${field}".`);
    }
    return { gte: new Date(fieldRange.gte), lte: new Date(fieldRange.lte) };
  }
}
